import { z } from "zod";
import { FormSection } from "@/components/v2/form-section";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SettingsPage } from "@/components/v2/settings-page";
import { CopyId } from "@/components/v2/copy-id";
import {
  useCurrentBusiness,
  useUpdateBusiness,
} from "@/hooks/use-current-business";
import { useCurrentUser } from "@/hooks/use-current-user";

const generalSchema = z.object({
  businessName: z
    .string()
    .min(2, "Business name must be at least 2 characters"),
  timezone: z.string().min(1, "Timezone is required"),
  currency: z.string().min(1, "Currency is required"),
});

const profileSchema = z.object({
  legalName: z.string().min(1, "Legal name is required"),
  addressLine1: z.string().min(1, "Address is required"),
  addressLine2: z.string().optional(),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  zip: z.string().min(5, "Valid ZIP code is required"),
  phone: z.string().min(10, "Valid phone number is required"),
});

type GeneralFormValues = z.infer<typeof generalSchema>;
type ProfileFormValues = z.infer<typeof profileSchema>;

const timezones = [
  { value: "America/New_York", label: "Eastern Time (ET)" },
  { value: "America/Chicago", label: "Central Time (CT)" },
  { value: "America/Denver", label: "Mountain Time (MT)" },
  { value: "America/Los_Angeles", label: "Pacific Time (PT)" },
  { value: "America/Anchorage", label: "Alaska Time (AKT)" },
  { value: "Pacific/Honolulu", label: "Hawaii Time (HT)" },
];

const currencies = [
  { value: "USD", label: "USD - US Dollar" },
  { value: "CAD", label: "CAD - Canadian Dollar" },
];

const usStates = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

function getString(
  settings: Record<string, unknown> | undefined,
  key: string,
): string {
  const v = settings?.[key];
  return typeof v === "string" ? v : "";
}

export default function V2SettingsGeneralPage() {
  const { data, isLoading, isError } = useCurrentBusiness();
  const update = useUpdateBusiness();
  const currentUser = useCurrentUser();

  return (
    <SettingsPage
      title="General"
      subtitle="Business identity, contact information, and branding."
    >
      {isLoading ? (
        <div className="text-sm text-[color:var(--color-text-md)]">
          Loading business settings…
        </div>
      ) : isError || !data?.business ? (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load business settings. Please reload the page.
        </div>
      ) : (
        (() => {
          const settings =
            (data.business.settings as Record<string, unknown> | undefined) ??
            {};
          const generalDefaults: GeneralFormValues = {
            businessName: data.business.name ?? "",
            timezone:
              (settings.timezone as string | undefined) ?? "America/New_York",
            currency: (settings.currency as string | undefined) ?? "USD",
          };
          const profileDefaults: ProfileFormValues = {
            legalName: getString(settings, "legal_name") || data.business.name,
            addressLine1: getString(settings, "address_line_1"),
            addressLine2: getString(settings, "address_line_2"),
            city: getString(settings, "city"),
            state: getString(settings, "state"),
            zip: getString(settings, "zip"),
            phone: getString(settings, "phone"),
          };
          return (
            <>
              <GeneralForm
                businessId={data.business.id}
                defaultValues={generalDefaults}
                isSaving={update.isPending}
                onSubmit={async (values) => {
                  await update.mutateAsync({
                    name: values.businessName,
                    settings: {
                      ...settings,
                      timezone: values.timezone,
                      currency: values.currency,
                    },
                  });
                }}
              />
              <ProfileForm
                businessId={data.business.id}
                defaultValues={profileDefaults}
                isSaving={update.isPending}
                onSubmit={async (values) => {
                  await update.mutateAsync({
                    settings: {
                      ...settings,
                      legal_name: values.legalName,
                      address_line_1: values.addressLine1,
                      address_line_2: values.addressLine2 ?? "",
                      city: values.city,
                      state: values.state,
                      zip: values.zip,
                      phone: values.phone,
                    },
                  });
                }}
              />
              <BusinessIdentityCard
                businessSlug={data.business.slug}
                businessId={currentUser?.businessId ?? data.business.id}
              />
              <BrandIdentitySection />
            </>
          );
        })()
      )}
    </SettingsPage>
  );
}

function BusinessIdentityCard({
  businessSlug,
  businessId,
}: {
  businessSlug: string | undefined;
  businessId: string | undefined;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="typo-heading">Business Identity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="typo-label text-[color:var(--color-text-md)] uppercase mb-1">
            Business Slug
          </p>
          {businessSlug ? (
            <CopyId
              variant="compact"
              value={businessSlug}
              ariaLabel="Copy business slug"
            />
          ) : (
            <p className="typo-mono text-sm text-[color:var(--color-text-hi)]">
              —
            </p>
          )}
        </div>
        <div>
          <p className="typo-label text-[color:var(--color-text-md)] uppercase mb-1">
            Business ID
          </p>
          {businessId ? (
            <CopyId
              variant="compact"
              value={businessId}
              ariaLabel="Copy business ID"
            />
          ) : (
            <p className="typo-mono text-sm text-[color:var(--color-text-hi)]">
              —
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GeneralForm({
  businessId,
  defaultValues,
  onSubmit,
  isSaving,
}: {
  businessId: string;
  defaultValues: GeneralFormValues;
  onSubmit: (values: GeneralFormValues) => Promise<void>;
  isSaving: boolean;
}) {
  return (
    <FormSection
      key={`general-${businessId}`}
      title="Business Details"
      description="Core settings for your business."
      schema={generalSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      isLoading={isSaving}
    >
      {(form) => (
        <>
          <FormField
            control={form.control}
            name="businessName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Timezone</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {timezones.map((tz) => (
                      <SelectItem key={tz.value} value={tz.value}>
                        {tz.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="currency"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Currency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select currency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </FormSection>
  );
}

function ProfileForm({
  businessId,
  defaultValues,
  onSubmit,
  isSaving,
}: {
  businessId: string;
  defaultValues: ProfileFormValues;
  onSubmit: (values: ProfileFormValues) => Promise<void>;
  isSaving: boolean;
}) {
  return (
    <FormSection
      key={`profile-${businessId}`}
      title="Business Profile"
      description="Legal entity and contact information."
      schema={profileSchema}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      isLoading={isSaving}
    >
      {(form) => (
        <>
          <FormField
            control={form.control}
            name="legalName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Legal Name</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="addressLine1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address Line 1</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="addressLine2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address Line 2</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>City</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {usStates.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="zip"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Zip</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </>
      )}
    </FormSection>
  );
}

function BrandIdentitySection() {
  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle className="typo-heading">Brand Identity</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="typo-label text-[color:var(--color-text-md)] mb-1">
            Brand Name
          </p>
          <p className="typo-body text-[color:var(--color-text-lo)]">
            Brand name configuration coming soon.
          </p>
        </div>
        <div>
          <p className="typo-label text-[color:var(--color-text-md)] mb-1">
            Logo
          </p>
          <div className="flex items-center justify-center h-24 rounded-md border-2 border-dashed border-[color:var(--color-border)] text-[color:var(--color-text-lo)] typo-body text-sm">
            Logo upload coming soon
          </div>
        </div>
        <div>
          <p className="typo-label text-[color:var(--color-text-md)] mb-1">
            Brand Color
          </p>
          <p className="typo-body text-[color:var(--color-text-lo)]">
            Color configuration coming soon.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
